<template>
  <b-modal :id="modalId" :ref="modalId" @show="reset" size="lg" :title="existingLevel ? 'Edit Sponsor Level' : 'Add Sponsor Level'" no-close-on-backdrop>
    <form autocomplete="off" class="w-full mt-2 grid grid-cols-1 gap-6">
      <div>
        <div class="mb-2">
          <label class="block text-gray-700 font-bold" for="level.name">
            Level Name
          </label>
          <div class="text-xs text-gray-600">Example: "Platinum Sponsor" or "Grand Champion".</div>
        </div>
        <input type="text" v-model="level.name" class="text-input focus:outline-none focus:shadow-outline" />
      </div>
      <div class="grid gap-4">
        <div class="mb-2">
          <label class="block text-gray-700 font-bold">
            Modifiers
          </label>
          <div class="text-xs text-gray-600">Allow multiple options for this sponsorship level. Often used for a list of breeds or show classes.</div>
          <div class="text-xs text-gray-600">Example: a level name of "Grand Champion" and modifier options of "Market Goat" and "Commercial Doe".</div>
        </div>
        <div class="flex">
          <input v-model="level.modifiersEnabled" v-bind:value="false" id="modifiersEnabledFalse" name="modifiersEnabledFalse" type="radio" checked class="mt-px focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
          <label  for="modifiersEnabledFalse" class="ml-3 block text-sm font-medium text-gray-700">
            Disable modifiers
          </label>
        </div>
        <div class="flex">
          <input v-model="level.modifiersEnabled" v-bind:value="true" id="modifiersEnabledTrue" name="modifiersEnabledTrue" type="radio" checked class="mt-px focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
          <label for="modifiersEnabledTrue" class="ml-3 block text-sm font-medium text-gray-700">
            Enable modifiers
          </label>
        </div>
      </div>
      <div v-if="level.modifiersEnabled">
        <label class="block text-gray-700 font-bold mb-4">
          Modifier Options (1 per line)
        </label>
        <textarea v-bind:value="level.modifiers.join('\n')" v-on:change="level.modifiers = $event.target.value.split('\n')" rows="4" class="text-input" placeholder="Example: Market Goat" />
      </div>
      <div>
        <div class="mb-2">
          <label class="block text-gray-700 font-bold">
            Cost
          </label>
          <div class="text-xs text-gray-600">Amount sponsors will pay.</div>
        </div>
        <tabs @selectedTab="onSelectedTab">
          <tab :name="SPONSOR_AMOUNT_TYPES.FIXED" :selected="level.amountType === SPONSOR_AMOUNT_TYPES.FIXED">
            <div class="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2" for="level.amountFixed">
                  Fixed {{ currencySymbol }} Amount
                </label>
                <input type="number" v-model.number="level.amountFixed" class="text-input focus:outline-none focus:shadow-outline" />
              </div>
              <div></div>
            </div>
          </tab>
          <tab :name="SPONSOR_AMOUNT_TYPES.RANGE" :selected="level.amountType === SPONSOR_AMOUNT_TYPES.RANGE">
            <div class="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2" for="level.amountMin">
                  Min {{ currencySymbol }} Amount
                </label>
                <input type="number" v-model.number="level.amountMin" class="text-input focus:outline-none focus:shadow-outline" />
              </div>
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2" for="level.amountMax">
                  Max {{ currencySymbol }} Amount
                </label>
                <input type="number" v-model.number="level.amountMax" class="text-input focus:outline-none focus:shadow-outline" />
                <div class="text-xs text-gray-600 mt-1">Set to {{currencySymbol}}0 for no maximum.</div>
              </div>
            </div>
          </tab>
          <tab :name="SPONSOR_AMOUNT_TYPES.OPEN" :selected="level.amountType === SPONSOR_AMOUNT_TYPES.OPEN">
            <div class="mt-4">Sponsors can pay any amount > {{ currencySymbol }}25.</div>
          </tab>
        </tabs>
      </div>
      <div>
        <div class="mb-4">
          <label class="block text-gray-700 font-bold">
            Number of Sponsors
          </label>
          <div class="text-xs text-gray-600">Specify number of sponsorships available for purchase.</div>
        </div>
        <div class="grid gap-4">
          <div class="flex">
            <input v-model="level.sponsorNumberType" :value="SPONSOR_NUMBER_TYPES.UNLIMITED" :id="SPONSOR_NUMBER_TYPES.UNLIMITED" name="sponsorNumberType" type="radio" checked class="mt-px focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
            <label :for="SPONSOR_NUMBER_TYPES.UNLIMITED" class="ml-3 block text-sm font-medium text-gray-700">
              Unlimited number of sponsors
            </label>
          </div>
          <div class="flex">
            <input v-model="level.sponsorNumberType" :value="SPONSOR_NUMBER_TYPES.LIMITED" :id="SPONSOR_NUMBER_TYPES.LIMITED" name="sponsorNumberType" type="radio" checked class="mt-px focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
            <label :for="SPONSOR_NUMBER_TYPES.LIMITED" class="ml-3 block text-sm font-medium text-gray-700">
              <div>Limit number of sponsors<span v-if="level.sponsorNumberType === SPONSOR_NUMBER_TYPES.LIMITED"> to:</span></div>
              <div v-if="level.sponsorNumberType === SPONSOR_NUMBER_TYPES.LIMITED" class="mt-2 w-24">
                <input type="number" v-model.number="level.sponsorNumber" :disabled="level.sponsorNumberType !== SPONSOR_NUMBER_TYPES.LIMITED" class="text-input focus:outline-none focus:shadow-outline" />
              </div>
            </label>
          </div>
        </div>
      </div>
      <div>
        <div class="mb-4">
          <label class="block text-gray-700 font-bold">
            Full Details
          </label>
          <div class="text-xs text-gray-600">Full description text for this sponsor level. Visible when purchasing a sponsorship.</div>
        </div>
        <textarea v-model="level.details" rows="5" class="text-input"/>
      </div>
      <div>
        <div class="mb-4">
          <label class="block text-gray-700 font-bold">
            Preview Text (Optional)
          </label>
          <div class="text-xs text-gray-600">A brief description for this sponsor level. If set, this preview text will show first before the sponsor choose the level. After selecting the level the full detail text will show.</div>
        </div>
        <textarea v-model="level.brief_details" rows="2" class="text-input"/>
      </div>
      <div>
        <div class="mb-4">
          <label class="block text-gray-700 font-bold">
            Hide by default
          </label>
          <div class="text-xs text-gray-600">Don't list these sponsors on the show homepage by default.</div>
        </div>
        <input type="checkbox" v-model="level.hideByDefault"/>
      </div>
    </form>
      <div slot="modal-footer" class="w-100">
        <button class="btn btn-gray-outline mr-3" variant="primary" @click="$refs[modalId].hide()">
            Cancel
        </button>
        <button v-if="existingLevel" class="btn btn-green" @click="saveSponsorLevel">Save Sponsor Level</button>
        <button v-else class="btn btn-green" @click="addSponsorLevel">Add Sponsor Level</button>
      </div>
  </b-modal>
</template>

<script>
import {SPONSOR_AMOUNT_TYPES, SPONSOR_NUMBER_TYPES} from './utils'

export default {
  props: {
    existingLevel: {
      type: Object,
      default: null
    },
    existingLevelNames: {
      type: Array,
      default: () => []
    },
    modalId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      level: {
        amountFixed: 0,
        amountMin: 0,
        amountMax: 0,
        amountType: SPONSOR_AMOUNT_TYPES.FIXED,
        details: '',
        modifiers: [],
        modifiersEnabled: false,
        name: '',
        sponsorNumber: 0,
        sponsorNumberType: SPONSOR_NUMBER_TYPES.UNLIMITED,
        sponsors: []
      }
    }
  },
  watch: {
    existingLevel: function (val) {
      if (val) {
        this.level = {...val}
        this.onSelectedTab(val.amountType)
      }
    }
  },
  computed: {
    event () {
      return this.$store.state.admin.adminData.event
    },
    hasDuplicateLevelName () {
      const levelsWithName = this.existingLevelNames.filter(n => n === this.level.name).length
      // if we are adding a level, there should be 0 with the new level's name
      // if we are editing a level, only one existing level should have that name
      return (levelsWithName > 0 && !this.existingLevel) || (levelsWithName > 0 && this.existingLevel && this.existingLevel.name !== this.level.name)
    },
    hasNoMinimumAmountSet () {
      return (this.level.amountType === SPONSOR_AMOUNT_TYPES.FIXED && this.level.amountFixed <= 0) || (this.level.amountType === SPONSOR_AMOUNT_TYPES.RANGE && this.level.amountMin <= 0)
    },
    isFormInvalid () {
      return !this.level.name || this.hasDuplicateLevelName || this.hasNoMinimumAmountSet
    },
    SPONSOR_AMOUNT_TYPES () {
      return SPONSOR_AMOUNT_TYPES
    },
    SPONSOR_NUMBER_TYPES () {
      return SPONSOR_NUMBER_TYPES
    },
    currencySymbol () {
      return this.Localized.getCurrencyInfo(this.event.location_details.country).symbol
    }
  },
  methods: {
    addSponsorLevel () {
      if (this.isFormInvalid) {
        return this.alertIfInvalid()
      }
      this.$emit('added-level', this.level)
      this.$refs[this.modalId].hide()
    },
    alertIfInvalid () {
      let alertMessage = ''
      if (this.hasDuplicateLevelName) {
        alertMessage = 'Level name is already in use'
      } else if (this.hasNoMinimumAmountSet) {
        alertMessage = 'Invalid cost value'
      } else {
        alertMessage = 'Level name is required'
      }
      alert(alertMessage)
    },
    onSelectedTab (tab) {
      this.level.amountType = tab
    },
    saveSponsorLevel () {
      if (this.isFormInvalid) {
        return this.alertIfInvalid()
      }
      this.$emit('edited-level', {oldLevel: this.existingLevel, newLevel: this.level})
      this.$refs[this.modalId].hide()
    },
    reset () {
      if (!this.existingLevel && this.level.name) {
        Object.assign(this.$data, this.$options.data.call(this))
      }
    }
  }
}
</script>
